import { useCallback, useRef, useState } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { AmplitudeProductEvent } from '@cfa/pathway-fe-shared';

import useAuth from '@/hooks/useAuth';

import { sendNativeEvent } from '@/utils/messageReactNative';
import { useRouteMatcher } from '@/utils/url';

import Constants from '@/constants';

import type { CertificateViewVariant } from './CertificateModals';
import type { PrintableCertificateData } from './PrintableCertificates';
import { serializeCertificatePrintDomForNative } from './serializeCertificatePrintDom';
import { usePrintCertificatesData } from './usePrintCertificatesData';

import { useAmplitudeTrack } from '@/amplitude/useAmplitude';

export interface UsePrintCertificatesOptions {
  planId?: string;
  teamMemberId?: string;
}

const FETCH_PROGRESS_MAX = 50;
const MINIMUM_LOADING_TIME_MS = 1500;

/**
 * Hook that manages certificate button visibility and click handling.
 *
 * Permission model:
 * - `/plans/my-plans` → always permitted (viewing own certificates)
 * - `/plans` → requires Trainer+ permissions
 * - `/reports` → requires Leader+ permissions
 */
export const usePrintCertificates = (options: UsePrintCertificatesOptions) => {
  const { planId, teamMemberId } = options;
  const { data } = useAuth();
  const track = useAmplitudeTrack();
  const routeMatches = useRouteMatcher();

  const isMyPlansRoute = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingMyPlansPath,
  );
  const isPlansRoute = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.TrainingPath,
  );
  const isReportsRoute = routeMatches.includes(
    Constants.ROUTE_PATH_GROUPS.ReportsPath,
  );

  const isAtLeastTrainer =
    (data?.allLocationsWithAtLeastTrainer.length ?? 0) > 0;
  const isAtLeastLeader =
    (data?.allLocationsWithAtLeastLeaderRoles.length ?? 0) > 0;

  const { certificatesEpic } = useFlags();

  const { certificateCount, isFetchingCount, fetchCertificates } =
    usePrintCertificatesData({
      planId,
      teamMemberId,
      enabled: certificatesEpic,
    });

  const [isLoadingModalOpen, setIsLoadingModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [printableCertificates, setPrintableCertificates] = useState<
    PrintableCertificateData[]
  >([]);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const domReadyResolverRef = useRef<(() => void) | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const intervalsRef = useRef<number[]>([]);

  const hasCertificates = certificateCount > 0;

  // Determine modal variant based on what was requested
  // - teamMember: one TM, multiple plans (requested by teamMemberId only)
  // - plan: one plan, multiple TMs (requested by planId only)
  // - single: base case
  const getViewModalVariant = (): CertificateViewVariant => {
    if (teamMemberId && !planId) return 'teamMember';
    if (planId && !teamMemberId) return 'plan';
    return 'single';
  };
  const viewModalVariant = getViewModalVariant();

  const hasPermission = isMyPlansRoute
    ? true
    : isPlansRoute
      ? isAtLeastTrainer
      : isReportsRoute
        ? isAtLeastLeader
        : false;

  // Show button only if feature flag is enabled AND user has permission AND there are certificates available
  const shouldShow =
    certificatesEpic &&
    hasPermission &&
    !!(planId ?? teamMemberId) &&
    hasCertificates;

  const handlePrint = () => {
    if (window.native.isNative) {
      const html = serializeCertificatePrintDomForNative();
      if (!html) {
        return;
      }
      track(AmplitudeProductEvent.CERTIFICATE_PRINT, {
        plan_id: planId,
        team_member_id: teamMemberId,
        certificate_count: printableCertificates.length,
        variant: viewModalVariant,
        is_native: window.native.isNative,
      });
      sendNativeEvent('print', { body: html, orientation: 'landscape' });
    } else {
      track(AmplitudeProductEvent.CERTIFICATE_PRINT, {
        plan_id: planId,
        team_member_id: teamMemberId,
        certificate_count: printableCertificates.length,
        variant: viewModalVariant,
        is_native: window.native.isNative,
      });
      window.print();
    }

    setIsViewModalOpen(false);
    setPrintableCertificates([]);
  };

  const handleClick = async () => {
    // Create abort controller for this operation
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setIsLoadingModalOpen(true);
    setLoadingProgress(0);
    const startTime = Date.now();

    // Phase 1: Fetching (0% to 50%)
    const progressInterval = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const targetProgress =
        FETCH_PROGRESS_MAX * (1 - Math.exp(-elapsed / 2000));
      setLoadingProgress(targetProgress);
    }, 50);
    intervalsRef.current.push(progressInterval);

    const certificates = await fetchCertificates();

    try {
      if (signal.aborted) return;

      clearInterval(progressInterval);
      setLoadingProgress(FETCH_PROGRESS_MAX);

      // Transform to printable format
      const printableCerts: PrintableCertificateData[] = certificates.map(
        cert => ({
          planName: cert.planName,
          teamMemberName: cert.teamMemberName,
          completionDateISO: cert.completionDate,
          expirationDateISO: cert.expirationDate ?? undefined,
          description: cert.description,
          completionDurationSeconds: cert.completionDurationSeconds,
        }),
      );

      // Phase 2: DOM rendering (50% to 100%)
      const domReadyPromise = new Promise<void>((resolve, reject) => {
        domReadyResolverRef.current = resolve;
        signal.addEventListener('abort', () =>
          reject(new DOMException('Aborted', 'AbortError')),
        );
      });

      setPrintableCertificates(printableCerts);

      // Progress is now updated via handleCertificatesProgress callback
      // as each certificate renders (50% to 100%)

      await domReadyPromise;

      setLoadingProgress(100);

      // Ensure minimum loading time has passed for better UX
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MINIMUM_LOADING_TIME_MS - elapsedTime);

      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }

      setIsLoadingModalOpen(false);
      setIsViewModalOpen(true);
    } catch (error) {
      // Clean up all intervals
      intervalsRef.current.forEach(clearInterval);
      intervalsRef.current = [];

      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }

      datadogRum.addError(error, {
        section: 'certificates',
        planId,
        teamMemberId,
        certificateCount,
      });
      setIsLoadingModalOpen(false);
      setLoadingProgress(0);
    }
  };

  const handleCloseViewModal = () => {
    setIsViewModalOpen(false);
    setPrintableCertificates([]);
  };

  const handleCancelLoading = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    intervalsRef.current.forEach(clearInterval);
    intervalsRef.current = [];
    domReadyResolverRef.current = null;
    setIsLoadingModalOpen(false);
    setLoadingProgress(0);
    setPrintableCertificates([]);
  }, []);

  const handleCertificatesReady = useCallback(() => {
    domReadyResolverRef.current?.();
    domReadyResolverRef.current = null;
  }, []);

  const handleCertificatesProgress = useCallback((progress: number) => {
    // Map 0-1 progress to 50-95% (leaving 5% for final transition)
    const DOM_PROGRESS_RANGE = 45;
    setLoadingProgress(FETCH_PROGRESS_MAX + progress * DOM_PROGRESS_RANGE);
  }, []);

  return {
    shouldShow,
    handleClick,
    isLoading: isFetchingCount,
    certificateCount,
    // Modal states
    isLoadingModalOpen,
    isViewModalOpen,
    loadingProgress,
    viewModalVariant,
    certificates: printableCertificates,
    handlePrint,
    handleCloseViewModal,
    handleCancelLoading,
    handleCertificatesReady,
    handleCertificatesProgress,
  };
};
